"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Sparkles,
  User as UserIcon,
  Search,
  X,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { Avatar } from "@/components/ui/Avatar";
import { Markdown } from "@/components/ui/Markdown";
import { formatRelative, cn } from "@/lib/utils";
import type { AgentRun, AgentSession, Issue, Doc, User } from "@/lib/types";

/**
 * Chat-style replay of agent runs in the active workspace, grouped by
 * session. Built for the question Rafa asked 2026-05-05:
 * "I don't remember which prompt spawned which task" — this view shows
 * every prompt → bullet (what the agent understood) → expandable raw
 * prompt + actions, with inline chips for every issue/doc the run touched.
 *
 * Different from `AgentRunsView` (which is a flat record list) and
 * `ActivityView` (which is the audit log). This is the conversational
 * surface — humans navigate by "what did I ask" not by "what changed".
 */
export function ChatTimelineView() {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allRuns = useStore((s) => s.agentRuns);
  const allSessions = useStore((s) => s.agentSessions);
  const allIssues = useStore((s) => s.issues);
  const allDocs = useStore((s) => s.docs);
  const users = useStore((s) => s.users);
  const selectIssue = useStore((s) => s.selectIssue);
  const selectDoc = useStore((s) => s.selectDoc);

  const [query, setQuery] = useState("");
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [requesterFilter, setRequesterFilter] = useState<string | null>(null);
  const [sessionFilter, setSessionFilter] = useState<string | null>(null);

  // Workspace-scoped data
  const runs = useMemo(
    () =>
      allRuns
        .filter((r) => r.workspaceId === activeWorkspaceId)
        .filter((r) => !agentFilter || r.actorId === agentFilter)
        .filter((r) => !sessionFilter || r.sessionId === sessionFilter)
        .filter(
          (r) =>
            !query ||
            (r.prompt ?? "").toLowerCase().includes(query.toLowerCase()) ||
            (r.summary ?? "").toLowerCase().includes(query.toLowerCase())
        )
        .filter((r) => {
          if (!requesterFilter) return true;
          // Surface as requester via touched issue's requester_id
          return r.touchedIssueIds.some((id) => {
            const iss = allIssues.find((i) => i.id === id);
            return iss?.requesterId === requesterFilter;
          });
        }),
    [allRuns, activeWorkspaceId, agentFilter, sessionFilter, requesterFilter, query, allIssues]
  );
  const sessions = useMemo(
    () => allSessions.filter((s) => s.workspaceId === activeWorkspaceId),
    [allSessions, activeWorkspaceId]
  );

  const sessionById = useMemo(
    () => new Map(sessions.map((s) => [s.id, s])),
    [sessions]
  );
  const issueById = useMemo(() => new Map(allIssues.map((i) => [i.id, i])), [allIssues]);
  const docById = useMemo(() => new Map(allDocs.map((d) => [d.id, d])), [allDocs]);
  // PAUT-59: build a {priorRunId → replacementRun} map so RunCard can render
  // the supersession arrow on the prior side. We scan workspace-scoped runs
  // (not just the filtered subset) so the arrow still points at the right
  // run even when filters hide it.
  const replacedByMap = useMemo(() => {
    const m = new Map<string, AgentRun>();
    for (const r of allRuns) {
      if (r.workspaceId === activeWorkspaceId && r.replacesRunId) {
        m.set(r.replacesRunId, r);
      }
    }
    return m;
  }, [allRuns, activeWorkspaceId]);
  const runById = useMemo(() => new Map(allRuns.map((r) => [r.id, r])), [allRuns]);

  // Group runs by session, newest run-time first
  const grouped = useMemo(() => {
    const map = new Map<string, AgentRun[]>();
    for (const r of runs) {
      const arr = map.get(r.sessionId) ?? [];
      arr.push(r);
      map.set(r.sessionId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => b.startedAt - a.startedAt);
    }
    return Array.from(map.entries()).sort(([, a], [, b]) => {
      const aLast = a[0]?.startedAt ?? 0;
      const bLast = b[0]?.startedAt ?? 0;
      return bLast - aLast;
    });
  }, [runs]);

  const agentChoices = useMemo(() => {
    const ids = new Set<string>();
    for (const r of runs) if (r.actorId) ids.add(r.actorId);
    return Array.from(ids).map((id) => users.find((u) => u.id === id)).filter(Boolean) as User[];
  }, [runs, users]);

  const requesterChoices = useMemo(() => {
    const ids = new Set<string>();
    for (const i of allIssues) if (i.requesterId && i.workspaceId === activeWorkspaceId) ids.add(i.requesterId);
    return Array.from(ids).map((id) => users.find((u) => u.id === id)).filter(Boolean) as User[];
  }, [allIssues, activeWorkspaceId, users]);

  const totalRuns = runs.length;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
        <MessageSquare size={14} className="text-[var(--accent)]" />
        <h2 className="text-[13.5px] font-semibold">Chat timeline</h2>
        <span className="text-[11px] text-[var(--fg-tertiary)]">
          {totalRuns} {totalRuns === 1 ? "run" : "runs"}
          {sessions.length > 0 && ` · ${sessions.length} session${sessions.length === 1 ? "" : "s"}`}
        </span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <div className="relative">
            <Search
              size={11}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search prompt or summary…"
              className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md pl-7 pr-2 py-1 text-[12px] outline-none focus:border-[var(--accent)] w-[220px]"
            />
          </div>
          <FilterDropdown
            label="Agent"
            value={agentFilter}
            options={agentChoices.map((u) => ({ id: u.id, label: u.name }))}
            onChange={setAgentFilter}
          />
          <FilterDropdown
            label="Requester"
            value={requesterFilter}
            options={requesterChoices.map((u) => ({ id: u.id, label: u.name }))}
            onChange={setRequesterFilter}
          />
          <FilterDropdown
            label="Session"
            value={sessionFilter}
            options={sessions.map((s) => ({ id: s.id, label: s.name }))}
            onChange={setSessionFilter}
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {grouped.length === 0 ? (
          <div className="text-center text-[12.5px] text-[var(--fg-muted)] mt-20">
            No agent runs in this workspace yet. As agents call{" "}
            <code className="text-[11px]">record_run</code> after completing
            tasks, conversations show up here.
          </div>
        ) : (
          <div className="max-w-[760px] mx-auto px-5 py-6 space-y-6">
            {grouped.map(([sessionId, sessionRuns]) => {
              const session = sessionById.get(sessionId);
              return (
                <SessionCluster
                  key={sessionId}
                  session={session ?? null}
                  runs={sessionRuns}
                  users={users}
                  issueById={issueById}
                  docById={docById}
                  replacedByMap={replacedByMap}
                  runById={runById}
                  onSelectIssue={selectIssue}
                  onSelectDoc={selectDoc}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SessionCluster({
  session,
  runs,
  users,
  issueById,
  docById,
  replacedByMap,
  runById,
  onSelectIssue,
  onSelectDoc,
}: {
  session: AgentSession | null;
  runs: AgentRun[];
  users: User[];
  issueById: Map<string, Issue>;
  docById: Map<string, Doc>;
  replacedByMap: Map<string, AgentRun>;
  runById: Map<string, AgentRun>;
  onSelectIssue: (id: string) => void;
  onSelectDoc: (id: string) => void;
}) {
  const sessionAuthor = session ? users.find((u) => u.id === session.authorId) : null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="space-y-3"
    >
      <header className="flex items-baseline gap-2 px-1">
        <Sparkles size={12} className="text-[var(--accent)]" />
        <h3 className="text-[12.5px] font-semibold text-[var(--fg)] truncate">
          {session?.name || "Untitled session"}
        </h3>
        <span className="text-[10.5px] text-[var(--fg-muted)]">
          {sessionAuthor?.name ?? "Unknown bot"} · {runs.length}{" "}
          {runs.length === 1 ? "run" : "runs"}
        </span>
      </header>
      <div className="space-y-2">
        {runs.map((r) => (
          <RunCard
            key={r.id}
            run={r}
            actor={users.find((u) => u.id === r.actorId) ?? null}
            issueById={issueById}
            docById={docById}
            replacedBy={replacedByMap.get(r.id) ?? null}
            replacesRun={r.replacesRunId ? runById.get(r.replacesRunId) ?? null : null}
            onSelectIssue={onSelectIssue}
            onSelectDoc={onSelectDoc}
          />
        ))}
      </div>
    </motion.section>
  );
}

function RunCard({
  run,
  actor,
  issueById,
  docById,
  replacedBy,
  replacesRun,
  onSelectIssue,
  onSelectDoc,
}: {
  run: AgentRun;
  actor: User | null;
  issueById: Map<string, Issue>;
  docById: Map<string, Doc>;
  replacedBy: AgentRun | null;
  replacesRun: AgentRun | null;
  onSelectIssue: (id: string) => void;
  onSelectDoc: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const touchedIssues = run.touchedIssueIds.map((id) => issueById.get(id)).filter(Boolean) as Issue[];
  const touchedDocs = run.touchedDocIds.map((id) => docById.get(id)).filter(Boolean) as Doc[];
  const actions = (run.actions as unknown as string[]) ?? [];
  const isSuperseded = replacedBy != null;

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--border-strong)] transition-colors">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 p-3 text-left"
      >
        <Avatar user={actor} size={22} />
        <div className={cn("flex-1 min-w-0 space-y-1.5", isSuperseded && "opacity-60")}>
          <div className="flex items-baseline gap-2 text-[11.5px]">
            <span className="font-medium text-[var(--fg)]">{actor?.name ?? "Unknown"}</span>
            {actor?.isBot && <span className="text-[10px] text-[var(--green)]">bot</span>}
            <span className="text-[var(--fg-muted)]">{formatRelative(run.startedAt)}</span>
            {run.durationMs ? (
              <span className="text-[var(--fg-muted)]">· {(run.durationMs / 1000).toFixed(1)}s</span>
            ) : null}
            {isSuperseded && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--orange)]/15 text-[var(--orange)] border border-[var(--orange)]/30"
                title={`Replaced by a later run — ${replacedBy?.summary?.slice(0, 80) ?? "no summary"}`}
              >
                → replaced
              </span>
            )}
            {replacesRun && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent-bg)] text-[var(--accent)] border border-[var(--accent)]/30"
                title={`Supersedes earlier run: ${replacesRun.summary?.slice(0, 80) ?? "no summary"}`}
              >
                🔁 supersedes
              </span>
            )}
            <span className="ml-auto text-[var(--fg-tertiary)]">
              {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </span>
          </div>
          {/* Bullet — what the agent understood / did */}
          <div
            className={cn(
              "text-[12.5px] text-[var(--fg-secondary)] leading-snug",
              isSuperseded && "line-through decoration-[var(--fg-muted)]"
            )}
          >
            {run.summary || (
              <span className="italic text-[var(--fg-muted)]">
                no summary
              </span>
            )}
          </div>
          {/* Touched chips — always visible */}
          {(touchedIssues.length > 0 || touchedDocs.length > 0) && (
            <div className="flex flex-wrap gap-1 pt-1">
              {touchedIssues.map((i) => (
                <button
                  key={i.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectIssue(i.id);
                  }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] bg-[var(--bg-tertiary)] border border-[var(--border)] hover:border-[var(--accent)] transition-colors"
                  title={i.title}
                >
                  <span className="font-mono text-[var(--fg-tertiary)]">
                    {i.identifier}
                  </span>
                  <span className="truncate max-w-[180px]">{i.title}</span>
                </button>
              ))}
              {touchedDocs.map((d) => (
                <button
                  key={d.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectDoc(d.id);
                  }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] bg-[var(--bg-tertiary)] border border-[var(--border)] hover:border-[var(--accent)] transition-colors"
                  title={d.title}
                >
                  <span>{d.icon}</span>
                  <span className="truncate max-w-[180px]">{d.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-[var(--border)] space-y-3 pt-3">
          {/* Raw prompt */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--fg-muted)] font-medium mb-1 flex items-center gap-1">
              <UserIcon size={10} /> Prompt
            </div>
            <div className="text-[12px] bg-[var(--bg-tertiary)] border border-[var(--border)] rounded p-2 font-mono text-[var(--fg-secondary)] whitespace-pre-wrap">
              {run.prompt || (
                <span className="italic text-[var(--fg-muted)]">no prompt recorded</span>
              )}
            </div>
          </div>
          {/* Actions list */}
          {actions.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--fg-muted)] font-medium mb-1">
                Actions ({actions.length})
              </div>
              <div className="space-y-1">
                {actions.map((a, i) => (
                  <div
                    key={i}
                    className="text-[12px] text-[var(--fg-secondary)] pl-3 border-l-2 border-[var(--border)]"
                  >
                    <Markdown content={a} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null;
  options: { id: string; label: string }[];
  onChange: (v: string | null) => void;
}) {
  if (options.length === 0) return null;
  const current = options.find((o) => o.id === value);
  return (
    <div className="relative">
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className={cn(
          "appearance-none bg-[var(--bg-tertiary)] border rounded-md pl-2 pr-6 py-1 text-[11.5px] outline-none focus:border-[var(--accent)] cursor-pointer",
          value
            ? "border-[var(--accent)] text-[var(--fg)]"
            : "border-[var(--border)] text-[var(--fg-tertiary)]"
        )}
      >
        <option value="">{label}: all</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      {current && (
        <button
          onClick={() => onChange(null)}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
          title="Clear filter"
        >
          <X size={10} />
        </button>
      )}
    </div>
  );
}
